import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import calendarService from "../../services/calendarService";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit2,
  Trash2,
  Calendar,
  AlertCircle,
  XCircle,
  CheckCircle2,
  Sparkles,
  Award,
  Clock,
  MapPin,
} from "lucide-react";
import "../../styles/CalendarPreview.css";

// Helper function to extract error message
const extractErrorMessage = (err, defaultMsg) => {
  if (err?.response?.data?.detail) {
    return err.response.data.detail;
  }
  if (err?.response?.data?.errors && Array.isArray(err.response.data.errors)) {
    return err.response.data.errors
      .map((item) => {
        const field = item.loc?.[1];
        const message = item.msg;
        return field ? `${field}: ${message}` : message;
      })
      .join("\n");
  }
  if (typeof err?.response?.data === "string") {
    return err.response.data;
  }
  if (typeof err?.response?.data?.detail === "string") {
    return err.response.data.detail;
  }
  if (err?.message) {
    return err.message;
  }
  return defaultMsg;
};

// Helper to get holiday ID
const getHolidayId = (holidayOrId) => {
  const rawId =
    typeof holidayOrId === "object"
      ? (
          holidayOrId?.holiday_id ??
          holidayOrId?.holidayId ??
          holidayOrId?.details?.holiday_id ??
          holidayOrId?.details?.holidayId ??
          holidayOrId?.id
        )
      : holidayOrId;

  if (rawId === null || rawId === undefined || rawId === "") {
    return null;
  }

  return typeof rawId === "string" && rawId.startsWith("holiday-")
    ? rawId.substring("holiday-".length)
    : rawId;
};

// Format Hijri date
const getHijriDate = (gregorianDate, holidayName) => {
  const date = new Date(gregorianDate);
  const hijriMonths = [
    "Muharram",
    "Safar",
    "Rabi' al-awwal",
    "Rabi' al-thani",
    "Jumada al-awwal",
    "Jumada al-thani",
    "Rajab",
    "Sha'ban",
    "Ramadan",
    "Shawwal",
    "Dhu al-Qi'dah",
    "Dhu al-Hijjah",
  ];

  const J = Math.floor((11 * date.getFullYear() + 3) / 30);
  const N = date.getDate() + 30 * (date.getMonth() + 1) - 2 - J;
  const Q = Math.floor(N / 10631);
  const R = N % 10631;
  const A = Math.floor((3 + 11 * Q + 30 * R) / 10592);
  const W = R % 354 + 30 * A - 1;
  const Q2 = Math.floor(W / 30);
  const Z = W % 30 + 1;

  const hijriYear = 30 * Q + A + 1;
  const hijriMonth = Q2;
  const hijriDay = Z;

  return `${hijriDay} ${hijriMonths[hijriMonth]} ${hijriYear}`;
};

const CalendarPreview = ({
  requests = [],
  holidays = [],
   showEmployeeName = false,
  onManageHoliday = () => {},
  onDateClick = (_date = "") => {},
}) => {
  const { user, isRH = true } = useAuth();

  // ==================== STATES ====================
  const [viewMode, setViewMode] = useState("month");
  const [currentMonthIdx, setCurrentMonthIdx] = useState(
    new Date().getFullYear() === 2026 ? new Date().getMonth() : 8
  );

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDetails, setShowEventDetails] = useState(false);

  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Holiday management
  const [showHolidayForm, setShowHolidayForm] = useState(false);
  const [showManageHolidaysModal, setShowManageHolidaysModal] = useState(false);
  const [allHolidaysList, setAllHolidaysList] = useState([]);
  const [holidaySearchQuery, setHolidaySearchQuery] = useState("");

  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayFormData, setHolidayFormData] = useState({
    name: "",
    date: "",
    holiday_type: "NATIONAL",
    description: "",
    duration_days: 1,
  });
  const [holidayFormError, setHolidayFormError] = useState(null);
  const [isSubmittingHoliday, setIsSubmittingHoliday] = useState(false);

  // ==================== PERMISSIONS ====================
  const isRHUser = user?.role?.toUpperCase() === "RH";
  const isManagerUser = user?.role?.toUpperCase() === "MANAGER";
  const isEmployeeUser = user?.role?.toUpperCase() === "EMPLOYEE";

  const canManageHolidays = isRHUser;
 //const canViewHolidays = true; // All roles can view
  const canViewHolidays = user?.role === "RH";

  // ==================== EFFECTS ====================
  useEffect(() => {
    loadAllHolidays();
  }, []);

  // ==================== FUNCTIONS ====================
  const loadAllHolidays = async () => {
    try {
      const data = await calendarService.getHolidays();
      setAllHolidaysList(data);
    } catch (err) {
      console.error("[CalendarPreview] Error loading holidays:", err);
    }
  };

  const handleAddHoliday = () => {
    if (!canManageHolidays) {
      alert("Vous n'avez pas les permissions pour ajouter un jour férié");
      return;
    }
    setEditingHoliday(null);
    setHolidayFormData({
      name: "",
      date: "",
      holiday_type: "NATIONAL",
      description: "",
      duration_days: 1,
    });
    setHolidayFormError(null);
    setShowHolidayForm(true);
  };

  const handleEditHoliday = (holiday) => {
    if (!canManageHolidays) {
      alert("Vous n'avez pas les permissions pour modifier un jour férié");
      return;
    }
    setEditingHoliday(holiday);
    setHolidayFormData({
      name: holiday.name || "",
      date: holiday.date ? String(holiday.date).substring(0, 10) : "",
      holiday_type: holiday.holiday_type || "NATIONAL",
      description: holiday.description || "",
      duration_days: holiday.duration_days || 1,
    });
    setHolidayFormError(null);
    setShowHolidayForm(true);
  };

  const handleSaveHoliday = async () => {
    if (!canManageHolidays) {
      alert("Vous n'avez pas les permissions pour enregistrer");
      return;
    }

    if (!holidayFormData.name.trim()) {
      setHolidayFormError("Le nom du jour férié est requis");
      return;
    }

    if (!holidayFormData.date) {
      setHolidayFormError("La date du jour férié est requise");
      return;
    }

    setIsSubmittingHoliday(true);
    setHolidayFormError(null);

    try {
      if (editingHoliday) {
        const holidayId = getHolidayId(editingHoliday);
        await calendarService.updateHoliday(holidayId, holidayFormData);
      } else {
        await calendarService.createHoliday(holidayFormData);
      }

      setShowHolidayForm(false);
      setEditingHoliday(null);
      setHolidayFormData({
        name: "",
        date: "",
        holiday_type: "NATIONAL",
        description: "",
        duration_days: 1,
      });

      if (onManageHoliday) await onManageHoliday();
      await loadAllHolidays();
    } catch (err) {
      console.error("[CalendarPreview] Error saving holiday:", err);
      const friendlyMsg = extractErrorMessage(
        err,
        "Erreur lors de l'enregistrement du jour férié."
      );
      setHolidayFormError(friendlyMsg);
    } finally {
      setIsSubmittingHoliday(false);
    }
  };

  const handleDeleteHoliday = async (holiday) => {
    if (!canManageHolidays) {
      alert("Vous n'avez pas les permissions pour supprimer");
      return;
    }

    const holidayId = getHolidayId(holiday);
    if (!holidayId) {
      alert("Impossible de trouver l'identifiant du jour férié.");
      return;
    }

    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce jour férié ?")) {
      return;
    }

    try {
      await calendarService.deleteHoliday(holidayId);
      await loadAllHolidays();
      if (onManageHoliday) await onManageHoliday();
    } catch (err) {
      console.error("[CalendarPreview] Error deleting holiday:", err);
      alert("Erreur lors de la suppression du jour férié");
    }
  };

  // ==================== RENDER ====================
  const monthNames = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  const daysInMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const currentDate = new Date(2026, currentMonthIdx, 1);
  const days = [];
  const startingDayOfWeek = firstDayOfMonth(currentDate);

  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }

  for (let i = 1; i <= daysInMonth(currentDate); i++) {
    days.push(i);
  }

  const filteredHolidays = allHolidaysList.filter((h) => {
    if (!holidaySearchQuery) return true;
    return h.name.toLowerCase().includes(holidaySearchQuery.toLowerCase());
  });

  return (
    <div className="calendar-preview-container">
      {/* ==================== CALENDAR HEADER ==================== */}
      <div className="calendar-header">
        <h3 className="calendar-title">📅 Calendrier {2026}</h3>
        
      </div>

      {/* ==================== CALENDAR GRID ==================== */}
      <div className="calendar-grid">
        <div className="calendar-controls">
          <button
            onClick={() =>
              setCurrentMonthIdx((prev) => (prev - 1 + 12) % 12)
            }
            className="btn-nav"
            title="Mois précédent"
          >
            <ChevronLeft size={18} />
          </button>
          <h4 className="current-month">
            {monthNames[currentMonthIdx]} 2026
          </h4>
          <button
            onClick={() => setCurrentMonthIdx((prev) => (prev + 1) % 12)}
            className="btn-nav"
            title="Mois suivant"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Calendar days header */}
        <div className="calendar-weekdays">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => (
            <div key={day} className="weekday-label">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="calendar-days">
          {days.map((day, idx) => {
            const dateStr = day
              ? `2026-${String(currentMonthIdx + 1).padStart(2, "0")}-${String(
                  day
                ).padStart(2, "0")}`
              : null;
            const dayHolidays = dateStr
              ? allHolidaysList.filter((h) => h.date === dateStr)
              : [];

            const dayRequests = dateStr
            ? requests.filter((req) => {
                if (!req.startDate || !req.endDate) return false;

                const startDate = String(req.startDate).substring(0, 10);
                const endDate = String(req.endDate).substring(0, 10);

                return dateStr >= startDate && dateStr <= endDate;
              })
            : [];  
                if (day === 1) {
                  console.log("[CALENDAR DEBUG]", {
                    month: currentMonthIdx + 1,
                    dateStr,
                    requestsReceived: requests.length,
                    requests: requests.map((r) => ({
                      id: r.id,
                      startDate: r.startDate,
                      endDate: r.endDate,
                      department:r.department,
                    })),
                    requestsForThisDay: dayRequests.length,
                  });
                }
            return (
              <div
                key={idx}
                className={`calendar-day ${!day ? "empty" : ""} ${
                  dayHolidays.length > 0 ? "has-holiday" : ""
                }`}
                onClick={() => day && onDateClick(dateStr)}
              >
                {day && (
                  <>
                    <span className="day-number">{day}</span>
                  {dayHolidays.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        marginTop: "5px",
                        width: "100%",
                      }}
                    >
                      <div className="holiday-indicator">
                        {dayHolidays.map((h) => (
                          
                          <span
                            key={h.id}
                            className="holiday-badge"
                            
                          >
                            🎉{h.name}
                          </span>
                          
                        ))}
                      </div>
                      </div>

                    )}

                  {dayRequests.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        marginTop: "5px",
                        width: "100%",
                      }}
                    >
                      {dayRequests.map((req) => (
                        <div
                          key={req.id}
                          title={`${showEmployeeName && req.employeeName ? req.employeeName + " - " : ""}${
                              req.type || "Congé"
                            } - ${req.status || ""}`}
                          style={{
                            background:
                              req.status === "Approuvé"
                                ? "#cee3f5"
                                : req.status === "Refusé"
                                ? "#fee2e2"
                                : "#fef3c7",

                            color:
                              req.status === "Approuvé"
                                ? "#166534"
                                : req.status === "Refusé"
                                ? "#991b1b"
                                : "#92400e",

                            border:
                              req.status === "Approuvé"
                                ? "1px solid #86c0ef"
                                : req.status === "Refusé"
                                ? "1px solid #fca5a5"
                                : "1px solid #fde68a",

                            borderRadius: "5px",
                            padding: "4px 6px",
                            fontSize: "11px",
                            fontWeight: "600",

                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                            {showEmployeeName && req.employeeName && (
                              <div style={{ fontWeight: "700" }}>
                                {req.employeeName}
                              </div>
                            )}

                            <div>
                              🏖️ {req.type || "Congé"}
                            </div>
                          
                        </div>
                      ))}
                    </div>
                  )}

                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ==================== HOLIDAYS LIST ==================== */}
      {canViewHolidays && (
        <div className="holidays-section">
          <div className="holidays-header">
            <h4>Jours Fériés ({allHolidaysList.length})</h4>
            <button
              onClick={() => setShowManageHolidaysModal(true)}
              className="btn-secondary"
            >
              <Calendar size={16} />
              Gérer tous les jours fériés
            </button>
          </div>

          {/* Holidays list */}
          <div className="holidays-list">
            {allHolidaysList.slice(0, 5).map((holiday) => (
              <div key={holiday.id} className="holiday-item">
                <div className="holiday-info">
                  <strong>{holiday.name}</strong>
                  <span className="holiday-date">
                    📅 {new Date(holiday.date).toLocaleDateString("fr-FR")}
                  </span>
                  {holiday.description && (
                    <span className="holiday-desc">{holiday.description}</span>
                  )}
                </div>
                {canManageHolidays && (
                  <div className="holiday-actions">
                    <button
                      onClick={() => handleEditHoliday(holiday)}
                      className="btn-icon-sm btn-info"
                      title="Modifier"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteHoliday(holiday)}
                      className="btn-icon-sm btn-danger"
                      title="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ==================== HOLIDAY FORM MODAL ==================== */}
      {showHolidayForm && canManageHolidays && (
        <div className="modal-overlay" onClick={() => setShowHolidayForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>
                {editingHoliday
                  ? "Modifier le jour férié"
                  : "Ajouter un jour férié"}
              </h4>
              <button
                onClick={() => setShowHolidayForm(false)}
                className="btn-close"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {holidayFormError && (
                <div className="alert alert-danger">
                  <AlertCircle size={16} />
                  <span>{holidayFormError}</span>
                </div>
              )}

              {/* Nom */}
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  value={holidayFormData.name}
                  onChange={(e) =>
                    setHolidayFormData({ ...holidayFormData, name: e.target.value })
                  }
                  placeholder="Ex. Fête de l'Indépendance"
                  className="form-input"
                />
              </div>

              {/* Date */}
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={holidayFormData.date}
                  onChange={(e) =>
                    setHolidayFormData({ ...holidayFormData, date: e.target.value })
                  }
                  className="form-input"
                />
              </div>

              {/* Type */}
              <div className="form-group">
                <label>Type</label>
                <select
                  value={holidayFormData.holiday_type}
                  onChange={(e) =>
                    setHolidayFormData({
                      ...holidayFormData,
                      holiday_type: e.target.value,
                    })
                  }
                  className="form-input"
                >
                  <option value="NATIONAL">National</option>
                  <option value="RELIGIOUS">Religieux</option>
                  <option value="COMPANY">Entreprise</option>
                </select>
              </div>

              {/* Durée */}
              <div className="form-group">
                <label>Durée (jours)</label>
                <input
                  type="number"
                  min="1"
                  value={holidayFormData.duration_days}
                  onChange={(e) =>
                    setHolidayFormData({
                      ...holidayFormData,
                      duration_days: parseInt(e.target.value) || 1,
                    })
                  }
                  className="form-input"
                />
              </div>

              {/* Description */}
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={holidayFormData.description}
                  onChange={(e) =>
                    setHolidayFormData({
                      ...holidayFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Description optionnelle..."
                  className="form-input"
                  rows={3}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowHolidayForm(false)}
                className="btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveHoliday}
                disabled={isSubmittingHoliday}
                className="btn-primary"
              >
                {isSubmittingHoliday ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MANAGE HOLIDAYS MODAL ==================== */}
      {showManageHolidaysModal && canViewHolidays && (
        <div className="modal-overlay" onClick={() => setShowManageHolidaysModal(false)}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Gestion des jours fériés</h4>
              <button
                onClick={() => setShowManageHolidaysModal(false)}
                className="btn-close"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Search */}
              {canManageHolidays && (
                <div className="search-bar">
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={holidaySearchQuery}
                    onChange={(e) => setHolidaySearchQuery(e.target.value)}
                    className="form-input"
                  />
                </div>
              )}

              {/* Holidays table */}
              <div className="table-responsive">
                <table className="holidays-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Durée</th>
                      <th>Description</th>
                      {canManageHolidays && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHolidays.map((holiday) => (
                      <tr key={holiday.id}>
                        <td>{holiday.name}</td>
                        <td>
                          {new Date(holiday.date).toLocaleDateString("fr-FR")}
                        </td>
                        <td>
                          <span className={`badge badge-${holiday.holiday_type?.toLowerCase()}`}>
                            {holiday.holiday_type}
                          </span>
                        </td>
                        <td>{holiday.duration_days} j.</td>
                        <td>{holiday.description || "—"}</td>
                        {canManageHolidays && (
                          <td>
                            <div className="action-buttons">
                              <button
                                onClick={() => handleEditHoliday(holiday)}
                                className="btn-icon-sm btn-info"
                                title="Modifier"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteHoliday(holiday)}
                                className="btn-icon-sm btn-danger"
                                title="Supprimer"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowManageHolidaysModal(false)}
                className="btn-secondary"
              >
                Fermer
              </button>
              {canManageHolidays && (
                <button
                  onClick={handleAddHoliday}
                  className="btn-primary"
                >
                  <Plus size={16} />
                  Ajouter un jour férié
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== PERMISSION ALERT ==================== 
      {!canManageHolidays && (
        <div className="permission-alert">
          <AlertCircle size={18} />
          <span>
            Vous avez un accès en lecture seule. Seul le rôle RH peut modifier
            les jours fériés.
          </span>
        </div>
      )}*/}
    </div>
  );
};

export default CalendarPreview;
